// payment-form.component.ts
import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
  ReactiveFormsModule
} from '@angular/forms';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { CurrencyPipe } from '@angular/common';

// Local Services
import { AppStateService } from '../../../app-state.service';
import { PaymentService } from '../payment.service';

// Directives & validators
import { PaymentCardTypeDirective } from '../payment-card-type.directive';
import { PaymentCardExpiryDirective } from '../payment-card-exp.directive';
import { UsZipcodeFormatDirective } from '../us-zipcode.directive';
import { PatientProfile } from '../../patient.model';
import { MatDialog } from '@angular/material/dialog';
import { HelpDialogComponent } from '../help-dialog.component';
import { ClinicInformation } from '../../clinic.model';

// Accept.js is loaded globally (e.g., in index.html), so declare it here.
declare var Accept: any;

/**
 * Luhn algorithm check for a numeric string (stripped of non-digits).
 */
function luhnCheck(value: string): boolean {
  const sanitized = value.replace(/\D/g, '');
  let sum = 0;
  let toggle = false;
  // Process digits from right-to-left:
  for (let i = sanitized.length - 1; i >= 0; i--) {
    let digit = parseInt(sanitized.charAt(i), 10);
    if (toggle) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    toggle = !toggle;
  }
  return sum % 10 === 0;
}

/**
 * Custom validator for card number Luhn check.
 */
function luhnValidator(control: AbstractControl): ValidationErrors | null {
  const value = (control.value || '').replace(/\D/g, '');
  if (!value) return null; // rely on "required" if it's empty
  return luhnCheck(value) ? null : { luhnInvalid: true };
}

/**
 * Single-field Expiration Date Validator
 * - Expects input in "MM/YY" format (e.g., "03/25").
 * - Checks 1 <= MM <= 12 and not expired.
 */
function singleExpDateValidator(control: AbstractControl): ValidationErrors | null {
  const val: string = (control.value || '').trim();

  // If empty, let "required" validator handle it.
  if (!val) return null;

  // Basic format check: exactly 5 chars, slash after 2 digits (e.g., "03/25").
  if (!/^\d{2}\/\d{2}$/.test(val)) {
    return { invalidExpiry: true };
  }

  // Parse month & year
  const [mm, yy] = val.split('/');
  const month = parseInt(mm, 10);
  const year2digit = parseInt(yy, 10);

  if (isNaN(month) || isNaN(year2digit)) {
    return { invalidExpiry: true };
  }

  // month range check
  if (month < 1 || month > 12) {
    return { invalidExpiry: true };
  }

  // Convert 2-digit year to 4-digit, naive approach: "20YY"
  const currentYear = new Date().getFullYear();
  const fullYear = 2000 + year2digit; // e.g., 2025 if "25"

  // Check if expired
  const currentMonth = new Date().getMonth() + 1;
  if (fullYear < currentYear) {
    return { expired: true };
  }
  if (fullYear === currentYear && month < currentMonth) {
    return { expired: true };
  }

  return null; // all good
}

@Component({
  standalone: true,
  selector: 'app-payment-form',
  templateUrl: './payment-form.component.html',
  styleUrls: ['./payment-form.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCardModule,
    MatSnackBarModule,
    MatIconModule,
    MatProgressSpinnerModule,
    CurrencyPipe,
    HelpDialogComponent,

    // Our custom directives
    PaymentCardTypeDirective,
    PaymentCardExpiryDirective,
    UsZipcodeFormatDirective
  ]
})
export class PaymentFormComponent implements OnInit {
  paymentForm!: FormGroup;
  patientBalance: number = 0;
  isProcessing: boolean = false;

  // Payment processing keys
  publicKey: string = '';
  apiLoginId: string = '';

  // clinic data
  clinicInfo: ClinicInformation;

  // Add a property to control visibility
  showCardNumber = false;

  // Inject services
  appStateService = inject(AppStateService);
  paymentService = inject(PaymentService);
  router = inject(Router);
  snackBar = inject(MatSnackBar);
  http = inject(HttpClient);
  private dialog = inject(MatDialog);

  // Organization and patient information
  orgId = '';
  clientId = '';
  patientId = '';
  clientInfo = {};
  patient = {} as PatientProfile;

  constructor() {
    // Get data from app state
    this.clinicInfo = this.appStateService.getState().clinicInfo || {} as ClinicInformation;
  }  

  ngOnInit(): void {
    // Load from global app state
    this.orgId = this.appStateService.getState().orgId || '';
    this.clientId = this.appStateService.getState().clientId || '';
    this.patientId = this.appStateService.getState().patientId || '';
    this.clientInfo = this.appStateService.getState().clinicInfo || {};
    this.patient = this.appStateService.getState().patient || {} as PatientProfile;

    // Get the patient balance
    this.getPatientBalance();

    // Build the form
    this.createForm();
    
    // Fetch public keys from backend
    this.getPublicKeys();
  }

  /**
   * Fetch the patient balance to pre-populate and provide context
   */
  getPatientBalance(): void {
    this.paymentService.fetchAccountAgingBalances(this.clientId, this.patientId).subscribe({
      next: (data: any) => {
        this.patientBalance = data.PatEstBal || 0;
        
        // Add a max validator to prevent paying more than the balance
        if (this.patientBalance > 0 && this.paymentForm) {
          const amountControl = this.paymentForm.get('amount');
          if (amountControl) {
            amountControl.setValidators([
              Validators.required, 
              Validators.pattern(/^\d+(\.\d{1,2})?$/),
              Validators.max(this.patientBalance)
            ]);
            amountControl.updateValueAndValidity();
          }
        }
      },
      error: (error) => {
      }
    });
  }

  /**
   * Initialize the Reactive Form with validators
   */
  private createForm() {
    const fb = new FormBuilder();
    this.paymentForm = fb.group({
      amount: [
        '',
        [Validators.required, Validators.pattern(/^\d+(\.\d{1,2})?$/)]
      ],
      cardholderName: [
        '',
        [Validators.required, Validators.minLength(2), Validators.maxLength(250)]
      ],
      cardNumber: ['', [Validators.required, luhnValidator]],
      expDate: ['', [Validators.required, singleExpDateValidator]],
      cardCode: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(4)]],
      billingZip: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(10)]]
    });
  }

  /**
   * Auto-fill the full balance amount
   */
  fillFullAmount(): void {
    if (this.patientBalance > 0) {
      this.paymentForm.get('amount')?.setValue(this.patientBalance.toFixed(2));
    }
  }

  /**
   * Get payment gateway public keys
   */
  getPublicKeys() {
    this.paymentService.getPaymentPublicKey(this.clientId).subscribe({
      next: (res) => {
        this.publicKey = (res.clientPublicKey || '').trim();
        this.apiLoginId = (res.apiLoginId || '').trim();
        if (!this.publicKey || !this.apiLoginId || !this.publicKey.length || !this.apiLoginId.length) {
          this.snackBar.open('Unable to process the request, please try again later', 'Close', { duration: 4000 });
          // this.router.navigate(['/dashboard', 'main']);
        }
      },
      error: (err) => {
        this.snackBar.open('Unable to process the request, please try again later!', 'Close', { duration: 4000 });
       //  this.router.navigate(['/dashboard', 'main']);
      }
    });
  }

  /**
   * Toggle card number visibility
   */
  toggleShowCardNumber(): void {
    this.showCardNumber = !this.showCardNumber;
  }

  /**
   * Submit handler - checks if we have keys and valid form,
   * then calls sendPaymentData() to tokenize.
   */
  onSubmit(): void {
    if (!this.publicKey || !this.apiLoginId) {
      this.snackBar.open('Failed to establish authentication', 'Close ', { duration: 4000 });
      return;
    }
    if (this.paymentForm.invalid) {
      // Mark all fields touched so errors display
      this.paymentForm.markAllAsTouched();
      return;
    }
    
    // Show processing state
    this.isProcessing = true;
    
    // If valid, go tokenize
    this.sendPaymentData();
  }

  /**
   * Gathers form data,
   * and dispatches to Accept.js for tokenization.
   */
  sendPaymentData() {
    // Auth data for Accept.js
    const authData = {
      clientKey: this.publicKey,
      apiLoginID: this.apiLoginId
    };

    // Retrieve form values
    const formValues = this.paymentForm.value;
    // Parse numerical amounts
    const totalAmount = parseFloat(formValues.amount) || 0;

    // Clean up card number (remove spaces/non-digits)
    const cardNumberNoSpaces = (formValues.cardNumber || '').replace(/\D/g, '');

    // Parse "MM/YY"
    const [mm, yy] = (formValues.expDate || '').split('/');

    // Build card data
    const cardData = {
      cardNumber: cardNumberNoSpaces,
      month: mm,
      // naive approach to year: "20" + yy => e.g. "2025" if "25"
      year: '20' + yy,
      cardCode: formValues.cardCode,
      zip: formValues.billingZip,
      fullName: formValues.cardholderName
    };

    const secureData = { authData, cardData };

    // Dispatch to Accept.js library to tokenize
    Accept.dispatchData(secureData, (response: any) => {
      this.responseHandler(response, totalAmount);
    });
  }

  /**
   * Handle Accept.js response. If success, we get a
   * one-time "opaqueData" token to send to our backend.
   */
  responseHandler(response: any, totalAmount: number) {
    this.isProcessing = false;
    
    if (response?.messages?.resultCode === 'Error') {
      this.snackBar.open(
        `Payment error: ${response.messages.message[0]?.text || 'Unknown error'}`,
        'Close',
        { duration: 4000 }
      );
    } else {
      const opaqueData = response.opaqueData;
      
      // Payload sent to your backend
      const payload = {
        patient_id: this.patientId,
        dataDescriptor: opaqueData.dataDescriptor,
        dataValue: opaqueData.dataValue,
        amount: totalAmount // pass the computed total
      };

      // Call your paymentService to finalize the payment server-side
      this.paymentService.postPayment(this.clientId, payload).subscribe({
        next: (res) => {
          this.snackBar.open('Your payment was successful! Thank you.', 'Close', { 
            duration: 5000,
            panelClass: 'success-snackbar'
          });
          this.paymentForm.reset();
          
          // Navigate back to account history to see updated balance
          this.router.navigate(['/dashboard', 'account-history']);
        },
        error: (err) => {
          const msg = err.error?.detail || 'Payment failed. Please try again or contact support.';
          this.snackBar.open(msg, 'Close', { 
            duration: 5000,
            panelClass: 'error-snackbar'
          });
        }
      });
    }
  }

  /**
   * Open help dialog for billing questions
   */
    openHelpDialog(): void {
      this.dialog.open(HelpDialogComponent, {
        width: '500px',
        data: {
          phoneNumber: '(555) 123-4567',
          emailAddress: 'billing@healthprovider.com',
          hours: 'Monday-Friday, 8:00 AM - 5:00 PM'
        }
      });
    }

  /**
   * Call the clinic phone number
   */
    callClinic(): void {
      if (!this.clinicInfo.phoneNumber) {
        this.showNotification('Clinic phone number is not available');
        return;
      }
      window.location.href = 'tel:' + this.clinicInfo.phoneNumber;
    }
  
    /**
     * Email the clinic
     */
    emailClinic(): void {
      if (!this.clinicInfo.email) {
        this.showNotification('Clinic email is not available');
        return;
      }
      window.location.href = 'mailto:' + this.clinicInfo.email;
    }

  /**
   * Show notification message
   */
    showNotification(message: string): void {
      this.snackBar.open(message, 'Close', {
        duration: 5000,
      });
    }
}